import {
  ActionList,
  ActionListItem,
  BasicButton,
  ButtonGroup,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Divider,
  FieldErrors,
  SectionMessage,
  Modal,
  ModalTrigger,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import {
  ArrayControlProps,
  composePaths,
  findUISchema,
  Layout,
} from "@jsonforms/core";
import { Fragment, useContext, useMemo } from "react";
import { FormValidationContext } from "react-aria-components";
import { Trans, useTranslation } from "react-i18next";

import { CascadiaJsonForm } from "../../../../forms/src/CascadiaJsonForm";
import {
  CascadiaJsonFormContent,
  CascadiaJsonFormSubmit,
} from "../../../../forms/src/CascadiaJsonForm";
import { CascadiaJsonFormContext } from "../../../../forms/src/CascadiaJsonFormContext";
import { Link } from "../../../../forms/src/components/Link";
import { CascadiaFormSubmissionError } from "../../../../forms/src/errors";
import { dataToFile } from "../../utils";
import {
  EmploymentDocumentItem,
  Employer,
  QualifyingEmployer,
  DocumentTypeOption,
} from "./types";
import {
  formatEmployerCardTitle,
  getEmploymentDocumentTypeLabel,
  getQualifyingEmployers,
} from "./utils";

function asString(arrayOrString: unknown) {
  return Array.isArray(arrayOrString) ? arrayOrString[0] : arrayOrString;
}

function assertEmploymentDocumentUploaded(
  values: EmploymentDocumentItem
): void | never {
  // Match EducationDocumentsControl: FileUpload ignores required, so trigger
  // CascadiaForm submitted state and let UploadControl show the required error.
  if (!values.employment_document) {
    throw new CascadiaFormSubmissionError({});
  }
}

function getDocumentItemLabels(
  document: EmploymentDocumentItem,
  documentTypes: DocumentTypeOption[]
) {
  const filename =
    dataToFile(asString(document.employment_document))?.name || "";
  const documentType = getEmploymentDocumentTypeLabel(
    String(document.document_type || ""),
    documentTypes
  );

  return { filename, documentType };
}

function getEmployerDocumentsDescription(
  employer: Employer,
  t: (key: string) => string
): string | null {
  if (employer.employment_type === "SELF_EMPLOYED") {
    return t(
      "As you were self-employed in this role, we're unable to verify your employment through an employer contact. Please upload documents that confirm your self-employment."
    );
  }
  if (employer.do_not_contact_employer) {
    return t(
      "You requested we don't contact this employer directly. Please upload documents that confirm your employment."
    );
  }
  return null;
}

export function EmploymentDocumentsControl(props: ArrayControlProps) {
  const { t } = useTranslation();
  const formValidation = useContext(FormValidationContext);
  const {
    locale,
    values: rootValues,
    isSubmitted,
  } = useContext(CascadiaJsonFormContext);
  const qualifyingEmployersFromOptions = (props.uischema?.options
    ?.qualifying_employers ?? []) as QualifyingEmployer[];
  const employers = (rootValues?.employment_history?.employers ||
    []) as Employer[];
  const qualifyingEmployers = getQualifyingEmployers(
    employers,
    qualifyingEmployersFromOptions
  );
  const documents = (props.data || []) as EmploymentDocumentItem[];
  const itemName =
    props.uischema.options?.itemName || props.label || t("Document");

  const errors = useMemo(() => {
    const submitErrors = formValidation[props.path];

    if (Array.isArray(submitErrors)) {
      return [...new Set(submitErrors)];
    }

    if (typeof submitErrors === "string" && submitErrors) {
      return [submitErrors];
    }

    return [];
  }, [formValidation, props.path]);

  const childUiSchema = useMemo(
    () =>
      findUISchema(
        props.uischemas || [],
        props.schema,
        props.uischema.scope,
        props.path,
        undefined,
        props.uischema,
        props.rootSchema
      ) as Layout | undefined,
    [
      props.uischemas,
      props.schema,
      props.uischema,
      props.path,
      props.rootSchema,
    ]
  );

  const documentFieldsUiSchema = childUiSchema;

  return (
    <Stack gap="space-4">
      {qualifyingEmployers.length === 0 && (
        <SectionMessage
          type="success"
          sectionTitle={t("No employment documents required")}
          icon="verified_user"
        >
          {t(
            "There are no employers that require supporting documents at this time. You can continue with your application."
          )}
        </SectionMessage>
      )}

      {qualifyingEmployers.map(({ employer, employerId, documentTypes }) => {
        const employerDocumentIndexes = documents
          .map((document, documentIndex) => ({ document, documentIndex }))
          .filter(({ document }) => document.employer_id === employerId);

        return (
          <EmployerDocumentsCard
            key={employerId}
            employer={employer}
            employerId={employerId}
            employerDocumentIndexes={employerDocumentIndexes}
            documents={documents}
            documentTypes={documentTypes}
            itemName={itemName}
            locale={locale}
            path={props.path}
            schema={props.schema}
            rootValues={rootValues}
            rootSchema={props.rootSchema}
            renderers={props.renderers}
            documentFieldsUiSchema={documentFieldsUiSchema}
            addItem={props.addItem}
            removeItems={props.removeItems}
          />
        );
      })}
      {isSubmitted && errors.length > 0 && <FieldErrors>{errors}</FieldErrors>}
    </Stack>
  );
}

function EmployerDocumentsCard({
  employer,
  employerId,
  employerDocumentIndexes,
  documents,
  documentTypes,
  itemName,
  locale,
  path,
  schema,
  rootValues,
  rootSchema,
  renderers,
  documentFieldsUiSchema,
  addItem,
  removeItems,
}: {
  employer: Employer;
  employerId: string;
  employerDocumentIndexes: {
    document: EmploymentDocumentItem;
    documentIndex: number;
  }[];
  documents: EmploymentDocumentItem[];
  documentTypes: DocumentTypeOption[];
  itemName: string;
  locale: string | undefined;
  path: string;
  schema: ArrayControlProps["schema"];
  rootValues: Record<string, unknown> | undefined;
  rootSchema: ArrayControlProps["rootSchema"];
  renderers: ArrayControlProps["renderers"];
  documentFieldsUiSchema: Layout | undefined;
  addItem: ArrayControlProps["addItem"];
  removeItems: ArrayControlProps["removeItems"];
}) {
  const { t } = useTranslation();
  const employerSummary = formatEmployerCardTitle(employer, t("Present"));
  const description = getEmployerDocumentsDescription(employer, t);

  return (
    <Card
      className={styles.card}
      hideBorder
      testID={`employment-documents-employer-${employerId}`}
    >
      <div className={styles.cardHeader}>
        <Text className={styles.cardTitle}>{employerSummary}</Text>
      </div>
      <Divider />
      <div className={styles.cardBody}>
        {description && (
          <Text
            style="global/body-secondary"
            color="text-weak"
            testID={`employment-documents-employer-${employerId}-description`}
          >
            {description}
          </Text>
        )}
        {employerDocumentIndexes.length > 0 && (
          <ActionList>
            {employerDocumentIndexes.map(
              ({ document, documentIndex }, listIndex) => {
                const { filename, documentType } = getDocumentItemLabels(
                  document,
                  documentTypes
                );
                const childPath = composePaths(path, `${documentIndex}`);

                return (
                  <Fragment key={childPath}>
                    <EmploymentDocumentItemRow
                      documentIndex={documentIndex}
                      childPath={childPath}
                      filename={filename}
                      documentType={documentType}
                      itemName={itemName}
                      locale={locale}
                      path={path}
                      schema={schema}
                      data={documents}
                      rootValues={rootValues}
                      rootSchema={rootSchema}
                      renderers={renderers}
                      documentFieldsUiSchema={documentFieldsUiSchema}
                      employerId={employerId}
                      addItem={addItem}
                      removeItems={removeItems}
                    />
                    {listIndex < employerDocumentIndexes.length - 1 && (
                      <Divider />
                    )}
                  </Fragment>
                );
              }
            )}
          </ActionList>
        )}

        <AddEmploymentDocumentButton
          employerId={employerId}
          employerSummary={employerSummary}
          itemName={itemName}
          locale={locale}
          path={path}
          schema={schema}
          data={documents}
          rootValues={rootValues}
          rootSchema={rootSchema}
          renderers={renderers}
          documentFieldsUiSchema={documentFieldsUiSchema}
          addItem={addItem}
        />
      </div>
    </Card>
  );
}

function EmploymentDocumentItemRow({
  documentIndex,
  childPath,
  filename,
  documentType,
  itemName,
  locale,
  path,
  schema,
  data,
  rootValues,
  rootSchema,
  renderers,
  documentFieldsUiSchema,
  employerId,
  addItem,
  removeItems,
}: {
  documentIndex: number;
  childPath: string;
  filename: string;
  documentType: string;
  itemName: string;
  locale: string | undefined;
  path: string;
  schema: ArrayControlProps["schema"];
  data: EmploymentDocumentItem[];
  rootValues: Record<string, unknown> | undefined;
  rootSchema: ArrayControlProps["rootSchema"];
  renderers: ArrayControlProps["renderers"];
  documentFieldsUiSchema: Layout | undefined;
  employerId: string;
  addItem: ArrayControlProps["addItem"];
  removeItems: ArrayControlProps["removeItems"];
}) {
  const document = data[documentIndex];

  return (
    <ActionListItem
      title={filename || itemName}
      trailing={
        <ModalTrigger>
          <Link
            color="text-weak"
            testID={`${slugify(itemName.toLowerCase())}-edit-link`}
          >
            <Trans>Edit</Trans>
          </Link>
          <Modal>
            <Dialog
              data-testid={`${slugify(itemName.toLowerCase())}-form-modal`}
            >
              {({ close }) => (
                <CascadiaJsonForm
                  locale={locale}
                  schema={schema}
                  uischema={documentFieldsUiSchema}
                  initialValues={document}
                  parentValues={data}
                  rootValues={rootValues}
                  rootSchema={rootSchema}
                  renderers={renderers}
                  onSubmit={async (values) => {
                    assertEmploymentDocumentUploaded(
                      values as EmploymentDocumentItem
                    );

                    const resolvedEmployerId =
                      document.employer_id ?? employerId;

                    if (removeItems) {
                      removeItems(path, [documentIndex])();
                    }

                    setTimeout(
                      addItem(path, {
                        ...(values as EmploymentDocumentItem),
                        employer_id: resolvedEmployerId,
                      })
                    );

                    close();
                  }}
                >
                  <DialogHeader
                    testID={slugify(itemName.toLowerCase())}
                    title={<Trans>Edit document</Trans>}
                    onClose={close}
                  />
                  <DialogContent>
                    <CascadiaJsonFormContent />
                  </DialogContent>
                  <DialogFooter
                    leftContent={
                      <Link
                        onPress={() => {
                          if (removeItems) {
                            removeItems(path, [documentIndex])();
                          }
                          close();
                        }}
                        color="text-weak"
                      >
                        <Trans>Delete</Trans>
                      </Link>
                    }
                  >
                    <BasicButton
                      testID={`${slugify(itemName.toLowerCase())}-cancel-button`}
                      onPress={close}
                    >
                      <Trans>Cancel</Trans>
                    </BasicButton>
                    <CascadiaJsonFormSubmit
                      testId={slugify(itemName.toLowerCase())}
                    >
                      <Trans>Save</Trans>
                    </CascadiaJsonFormSubmit>
                  </DialogFooter>
                </CascadiaJsonForm>
              )}
            </Dialog>
          </Modal>
        </ModalTrigger>
      }
    >
      <Text style="global/body-secondary" color="text-weak">
        {documentType}
      </Text>
    </ActionListItem>
  );
}

function AddEmploymentDocumentButton({
  employerId,
  employerSummary,
  itemName,
  locale,
  path,
  schema,
  data,
  rootValues,
  rootSchema,
  renderers,
  documentFieldsUiSchema,
  addItem,
}: {
  employerId: string;
  employerSummary: string;
  itemName: string;
  locale: string | undefined;
  path: string;
  schema: ArrayControlProps["schema"];
  data: EmploymentDocumentItem[];
  rootValues: Record<string, unknown> | undefined;
  rootSchema: ArrayControlProps["rootSchema"];
  renderers: ArrayControlProps["renderers"];
  documentFieldsUiSchema: Layout | undefined;
  addItem: ArrayControlProps["addItem"];
}) {
  return (
    <ModalTrigger>
      <BasicButton
        testID={`employment-documents-add-${employerId}`}
        leftIcon="add"
      >
        <Trans>Add document</Trans>
      </BasicButton>
      <Modal>
        <Dialog>
          {({ close }) => (
            <CascadiaJsonForm
              locale={locale}
              schema={schema}
              uischema={documentFieldsUiSchema}
              initialValues={{ employer_id: employerId }}
              parentValues={data}
              rootValues={rootValues}
              rootSchema={rootSchema}
              renderers={renderers}
              onSubmit={async (values) => {
                assertEmploymentDocumentUploaded(
                  values as EmploymentDocumentItem
                );

                addItem(path, {
                  ...(values as EmploymentDocumentItem),
                  employer_id: employerId,
                })();
                close();
              }}
            >
              <DialogHeader
                title={employerSummary}
                onClose={close}
                testID={`employment-documents-add-${employerId}-header`}
              />
              <DialogContent>
                <CascadiaJsonFormContent />
              </DialogContent>
              <DialogFooter>
                <ButtonGroup justify="end">
                  <BasicButton
                    testID={`employment-documents-add-${employerId}-cancel`}
                    onPress={close}
                  >
                    <Trans>Cancel</Trans>
                  </BasicButton>
                  <CascadiaJsonFormSubmit
                    testId={`employment-documents-add-${employerId}`}
                  >
                    <Trans>Save</Trans>
                  </CascadiaJsonFormSubmit>
                </ButtonGroup>
              </DialogFooter>
            </CascadiaJsonForm>
          )}
        </Dialog>
      </Modal>
    </ModalTrigger>
  );
}

const styles = {
  card: css({
    padding: 0,
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-3)",
  }),
  cardHeader: css({
    background: "var(--background-accent-neutral-weakest, #F7F8F8)",
    padding: "var(--space-4)",
  }),
  cardTitle: css({
    fontSize: "var(--font-size-4)",
    fontWeight: 600,
  }),
  cardBody: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
};
